import { useApolloClient } from '@apollo/client';
import { GridFilterModel, GridRowParams } from '@mui/x-data-grid-pro';
import { first, isEmpty } from 'lodash';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  SmallTableEmptyState,
  SmallTableEmptyStateFill,
} from '@/components/display/SmallTableEmptyState/SmallTableEmptyState';
import { Button } from '@/components/form/baseInputs/Button';
import { FileSearch02Icon } from '@/components/icons/FileSearch02Icon';
import { Upload01Icon } from '@/components/icons/Upload01Icon';
import { useFeedback } from '@/components/notifications/Feedback/useFeedback';
import { TableEmptyState } from '@/components/tables/DataTable/components/TableEmptyState';
import { DataTable } from '@/components/tables/DataTable/DataTable';
import { useReportError } from '@/hooks/useReportError';
import { useCanEditHouseholdData } from '@/modules/authentication/hooks/useCanEditHouseholdData';
import { EntityType } from '@/modules/entities/types/EntityType';
import { useOnHouseholdWorkflowTrackerComplete } from '@/modules/workflowTrackers/hooks/useOnHouseholdWorkflowTrackerComplete';
import { useOnWorkflowTrackerComplete } from '@/modules/workflowTrackers/hooks/useOnWorkflowTrackerComplete';
import { ROUTE_KEYS } from '@/navigation/constants';
import { getCompletePathFromRouteKey } from '@/navigation/navigationUtils';
import { COLORS } from '@/styles/tokens/colors';
import {
  DocumentOrderField,
  DocumentType,
  DocumentWhereInput,
  OrderDirection,
  WorkflowTrackerKind,
} from '@/types/schema';
import { diagnostics } from '@/utils/diagnostics';
import { getNodes } from '@/utils/graphqlUtils';

import { DocumentUploaderModal } from '../DocumentUploaderModal/DocumentUploaderModal';
import { useMultiDocumentUploaderContext } from '../MultiDocumentUploader/context/multiDocumentUploader.context';
import {
  DocumentsTableVariant,
  useDocumentsTableContext,
} from './context/DocumentsTable.context';
import { DocumentsTableProvider } from './context/DocumentsTable.provider';
import { DocumentsSearchFilterToolbar } from './DocumentsSearchFilterToolbar/DocumentsSearchFilterToolbar';
import { DocumentsTableRowData } from './DocumentsTable.types';
import { useDocumentsTableDocumentsQuery } from './graphql/DocumentsTable.generated';
import { useMapDocumentsToRowsAndColumns } from './hooks/useMapDocumentsToRowsAndColumns';

interface DocumentsTableProps {
  householdId: string;
  documentsLike: DocumentWhereInput;

  // if you want to force associations between created documents and a specific entity
  // or strategy, pass the appropriate ID along with the appropriate variant
  entityId?: string;
  entityType?: EntityType | undefined | null;

  // createDocumentModalOpen and handleCreateDocumentModalClose are used to
  // allow an external component to handle exposing their own "create document"
  // button and open the model from within here.
  createDocumentModalOpen?: boolean;
  handleCreateDocumentModalClose?: () => void;
  variant: DocumentsTableVariant;
  /**
   * How the no-documents empty state fills vertical space. Only set to
   * `'viewport'` on full-page usages; leave unset when the table is mounted in a
   * height-constrained container (e.g. an entity detail card) so the empty state
   * doesn't balloon the card.
   */
  emptyStateFill?: SmallTableEmptyStateFill;
}

function getFilterVariables(
  searchValue: string,
  filterValue: Record<'documentType', DocumentType[]>
): DocumentWhereInput {
  const where: DocumentWhereInput = {};

  if (searchValue) {
    where.nameContainsFold = searchValue;
  }

  if (filterValue.documentType?.length > 0) {
    where.typeIn = filterValue.documentType;
  }

  return where;
}

const QUERY_NAME = 'DocumentsTableDocuments';

function DocumentsTableInner({
  householdId,
  entityId,
  entityType,
  documentsLike,
  createDocumentModalOpen,
  handleCreateDocumentModalClose,
  emptyStateFill,
}: DocumentsTableProps) {
  const apolloClient = useApolloClient();
  const canEdit = useCanEditHouseholdData();
  const { createErrorFeedback } = useFeedback();
  const { reportError } = useReportError();
  const { showFeedback } = useFeedback();
  const navigate = useNavigate();
  const [documentModalOpen, setDocumentModalOpen] = useState(false);

  const { clearUploadedFiles, uploadDocuments } =
    useMultiDocumentUploaderContext();
  const [loading, setLoading] = useState<boolean>(false);

  const [searchValue, setSearchValue] = useState('');
  const { filterValue } = useDocumentsTableContext();

  const onFilterModelChange = useCallback(
    ({ quickFilterValues }: GridFilterModel) => {
      const searchString: string = quickFilterValues?.[0];
      setSearchValue(searchString ?? '');
    },
    []
  );

  useEffect(() => {
    if (!createDocumentModalOpen || documentModalOpen) return;
    setDocumentModalOpen(true);
  }, [documentModalOpen, createDocumentModalOpen, setDocumentModalOpen]);

  const {
    data,
    loading: loadingDocuments,
    refetch,
  } = useDocumentsTableDocumentsQuery({
    fetchPolicy: 'cache-and-network',
    variables: {
      where: {
        ...getFilterVariables(searchValue, filterValue),
        ...documentsLike,
        typeNotIn: [DocumentType.GeneratedDocument],
      },
      orderBy: [
        { field: DocumentOrderField.CreatedAt, direction: OrderDirection.Desc },
      ],
    },
    onError: createErrorFeedback(
      `We weren't able to load your documents. Please refresh the page to try again.`
    ),
  });

  const { columns, rows } = useMapDocumentsToRowsAndColumns(
    getNodes(data?.documents),
    {
      refetch,
      entityId,
      householdId,
    }
  );

  // Watch for document summary completion and refetch
  useOnHouseholdWorkflowTrackerComplete({
    kind: WorkflowTrackerKind.DocumentSummary,
    onCompleted: () => {
      diagnostics.debug(
        '[DocumentsTable] Document summary processing completed, refetching',
        { householdId }
      );
      void refetch();
    },
  });

  // Watch for document processing completion and refetch
  useOnWorkflowTrackerComplete({
    kind: WorkflowTrackerKind.DocumentSplitBinder,
    onCompleted: () => {
      diagnostics.debug(
        '[DocumentsTable] Document processing completed, refetching',
        { householdId }
      );
      void refetch();
    },
  });

  const onRowClick = useCallback(
    (
      { row }: GridRowParams<DocumentsTableRowData>,
      event: React.MouseEvent
    ) => {
      const path = entityId
        ? getCompletePathFromRouteKey(
            ROUTE_KEYS.HOUSEHOLD_ENTITY_DOCUMENT_DETAILS,
            {
              householdId,
              entityId,
              documentId: row.id,
            }
          )
        : getCompletePathFromRouteKey(
            ROUTE_KEYS.HOUSEHOLD_DETAILS_DOCUMENT_DETAILS,
            {
              householdId,
              documentId: row.id,
            }
          );

      // we're in a entity-specific documents table, so we need to navigate to the entity-specific document details page
      const openInNewTab = event?.metaKey || event?.ctrlKey;
      if (openInNewTab) {
        window.open(path, '_blank');
        return;
      }

      navigate(path);
    },
    [entityId, householdId, navigate]
  );

  const onSubmit = useCallback(async () => {
    setLoading(true);
    let documentIds: string[] = [];
    try {
      documentIds = await uploadDocuments({
        householdId,
        defaultDocumentType: DocumentType.Other,
        entityId,
        entityType,
      });

      clearUploadedFiles();
      setDocumentModalOpen(false);
      handleCreateDocumentModalClose?.();

      await apolloClient.refetchQueries({
        include: [QUERY_NAME],
      });

      const firstDocumentId = first(documentIds);

      if (documentIds.length === 1 && firstDocumentId) {
        if (entityId) {
          navigate(
            getCompletePathFromRouteKey(
              ROUTE_KEYS.HOUSEHOLD_ENTITY_DOCUMENT_DETAILS,
              {
                householdId,
                entityId,
                documentId: firstDocumentId,
              }
            )
          );
        } else {
          navigate(
            getCompletePathFromRouteKey(
              ROUTE_KEYS.HOUSEHOLD_DETAILS_DOCUMENT_DETAILS,
              {
                householdId,
                documentId: firstDocumentId,
              }
            )
          );
        }
      }
    } catch (e) {
      reportError('Caught error while uploading documents', e as Error);
      showFeedback('Error uploading documents.  Please try again later.');
    } finally {
      setLoading(false);
    }
  }, [
    apolloClient,
    clearUploadedFiles,
    entityId,
    entityType,
    handleCreateDocumentModalClose,
    householdId,
    navigate,
    reportError,
    showFeedback,
    uploadDocuments,
  ]);

  const hasFilters = searchValue || filterValue.documentType?.length > 0;
  const showEmptyState = !loadingDocuments && isEmpty(rows);

  return (
    <>
      {showEmptyState && !hasFilters && (
        <SmallTableEmptyState
          fill={emptyStateFill}
          icon={<FileSearch02Icon size={32} color={COLORS.TEXT.PRIMARY} />}
          heading="No documents"
          description="Store all of your client's estate planning documents and automatically summarize trust documents with Luminary AI"
          action={
            canEdit ? (
              <Button
                variant="primary"
                size="sm"
                startIcon={Upload01Icon}
                onClick={() => setDocumentModalOpen(true)}
              >
                Upload documents
              </Button>
            ) : undefined
          }
        />
      )}
      <DocumentUploaderModal
        isOpen={documentModalOpen}
        onSubmit={onSubmit}
        onClose={() => {
          clearUploadedFiles();
          setDocumentModalOpen(false);
          handleCreateDocumentModalClose?.();
        }}
        householdId={householdId}
        loading={loading}
      />
      {(!showEmptyState || hasFilters) && (
        <DataTable
          rows={rows}
          onRowClick={onRowClick}
          columns={columns}
          loading={loadingDocuments}
          pageSize={200}
          onFilterModelChange={onFilterModelChange}
          filterMode="server"
          initialState={{
            columns: {
              columnVisibilityModel: {
                associatedEntity: !entityId,
              },
            },
            sorting: {
              sortModel: [{ field: 'createdAt', sort: 'desc' }],
            },
          }}
          slots={{
            toolbar: DocumentsSearchFilterToolbar,
            noRowsOverlay: () => (
              <TableEmptyState
                text={
                  hasFilters
                    ? 'No documents match your search or filters'
                    : 'No documents'
                }
              />
            ),
          }}
          sortingOrder={['asc', 'desc']}
        />
      )}
    </>
  );
}

export function DocumentsTable(props: DocumentsTableProps) {
  return (
    <DocumentsTableProvider variant={props.variant}>
      <DocumentsTableInner {...props} />
    </DocumentsTableProvider>
  );
}
